const sectionResponseCache = new Map();
const SECTION_CACHE_MAX = 8;

if (!customElements.get('product-v2')) {
  customElements.define(
    'product-v2',
    class ProductV2 extends HTMLElement {
      constructor() {
        super();
        this.abortController = null;
        this.onColorClick = this.onColorClick.bind(this);
        this.onOptionValueChange = this.onOptionValueChange.bind(this);
        this.onColorPrefetch = this.onColorPrefetch.bind(this);
        this.productVariants = null;
      }

      connectedCallback() {
        this.addEventListener('click', this.onColorClick);
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.onOptionValueChange
        );

        if (this.dataset.skipInitialSync === 'true') {
          delete this.dataset.skipInitialSync;
          return;
        }

        this.initColorPrefetch();
        this.runInitialSync();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onColorClick);
        this.onVariantChangeUnsubscriber?.();
        this.abortController?.abort();
      }

      onOptionValueChange({ data: { event, target } }) {
        if (!this.contains(event.target)) return;
        if (target.type !== 'radio') return;

        this.updateOptionAvailability();
        if (this.clearInvalidSelections()) {
          this.updateOptionAvailability();
        }

        const matchedVariant = this.findMatchingVariant();
        this.syncBuyRowState(matchedVariant);
        this.updateURL(this.dataset.url, matchedVariant?.id);

        if (matchedVariant?.id) {
          this.fetchPrice(matchedVariant.id);
        }
      }

      getVariantSelects() {
        return this.querySelector('variant-selects');
      }

      getRequiredOptionCount() {
        return parseInt(this.getVariantSelects()?.dataset.requiredOptionCount || '0', 10);
      }

      getSelectedOptionCount() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return 0;

        return Array.from(variantSelects.querySelectorAll('.product-v2-variant-picker__group')).filter((group) =>
          group.querySelector('input[type="radio"]:checked')
        ).length;
      }

      loadProductVariants() {
        if (this.productVariants) return this.productVariants;

        const variantsJson = this.querySelector('[data-product-variants]')?.textContent?.trim();
        if (!variantsJson) {
          this.productVariants = [];
          return this.productVariants;
        }

        try {
          this.productVariants = JSON.parse(variantsJson);
        } catch (error) {
          this.productVariants = [];
        }

        return this.productVariants;
      }

      getVariantOptionValue(variant, position) {
        if (Array.isArray(variant.options) && variant.options.length >= position) {
          return variant.options[position - 1];
        }

        return variant[`option${position}`] ?? null;
      }

      getSelectedOptionsByPosition() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return {};

        const selectedByPosition = {};

        variantSelects.querySelectorAll('.product-v2-variant-picker__group').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          const checked = group.querySelector('input[type="radio"]:checked');
          if (!Number.isNaN(position) && checked) {
            selectedByPosition[position] = checked.value;
          }
        });

        return selectedByPosition;
      }

      findMatchingVariant() {
        const selectedByPosition = this.getSelectedOptionsByPosition();
        const requiredCount = this.getRequiredOptionCount();
        const selectedCount = Object.keys(selectedByPosition).length;

        if (requiredCount > 0 && selectedCount < requiredCount) return null;

        const variants = this.loadProductVariants();
        if (!variants.length) return null;

        return (
          variants.find((variant) =>
            Object.entries(selectedByPosition).every(
              ([position, value]) => this.getVariantOptionValue(variant, parseInt(position, 10)) === value
            )
          ) || null
        );
      }

      isVariantAvailable(variant) {
        return Boolean(variant) && variant.available !== false;
      }

      getOptionState(position, optionValue, selectedByPosition) {
        const variants = this.loadProductVariants();
        const matchingVariants = variants.filter((variant) => {
          if (this.getVariantOptionValue(variant, position) !== optionValue) return false;

          return Object.entries(selectedByPosition).every(([otherPosition, otherValue]) => {
            if (parseInt(otherPosition, 10) === position) return true;
            return this.getVariantOptionValue(variant, parseInt(otherPosition, 10)) === otherValue;
          });
        });

        if (!matchingVariants.length) return 'unavailable';
        if (matchingVariants.some((variant) => this.isVariantAvailable(variant))) return 'available';
        return 'sold-out';
      }

      autoSelectDefaultOptions() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return false;

        const groups = Array.from(variantSelects.querySelectorAll('.product-v2-variant-picker__group')).sort(
          (a, b) => parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10)
        );

        let changed = false;

        groups.forEach((group) => {
          if (group.querySelector('input[type="radio"]:checked')) return;

          const position = parseInt(group.dataset.optionPosition, 10);
          if (Number.isNaN(position)) return;

          const inputs = Array.from(group.querySelectorAll('input[type="radio"]')).filter(
            (input) => !input.closest('.product-v2-variant-picker__option')?.hidden
          );
          if (!inputs.length) return;

          const selectedByPosition = this.getSelectedOptionsByPosition();
          const availableInput = inputs.find(
            (input) => this.getOptionState(position, input.value, selectedByPosition) === 'available'
          );
          const targetInput = availableInput || inputs[0];

          targetInput.checked = true;
          changed = true;
        });

        return changed;
      }

      clearInvalidSelections() {
        let changed = false;

        this.getVariantSelects()?.querySelectorAll('.product-v2-variant-picker__group').forEach((group) => {
          const checked = group.querySelector('input[type="radio"]:checked');
          if (!checked) return;

          const optionEl = checked.closest('.product-v2-variant-picker__option');
          if (optionEl?.hidden) {
            checked.checked = false;
            changed = true;
          }
        });

        return changed;
      }

      optionValueExists(position, value) {
        return this.loadProductVariants().some(
          (variant) => this.getVariantOptionValue(variant, position) === value
        );
      }

      restoreSelectedOptions(savedOptions) {
        if (!savedOptions || !Object.keys(savedOptions).length) return false;

        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return false;

        let changed = false;

        Object.entries(savedOptions).forEach(([position, value]) => {
          const positionNum = parseInt(position, 10);
          if (Number.isNaN(positionNum) || !value) return;
          if (!this.optionValueExists(positionNum, value)) return;

          const group = variantSelects.querySelector(
            `.product-v2-variant-picker__group[data-option-position="${position}"]`
          );
          if (!group) return;

          const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((el) => el.value === value);
          if (!input || input.checked) return;

          input.checked = true;
          changed = true;
        });

        return changed;
      }

      updateOptionAvailability() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return;

        const variants = this.loadProductVariants();
        if (!variants.length) return;

        const selectedByPosition = this.getSelectedOptionsByPosition();
        const hasSelectionInOtherGroup = (position) =>
          Object.keys(selectedByPosition).some((selectedPosition) => parseInt(selectedPosition, 10) !== position);

        variantSelects.querySelectorAll('.product-v2-variant-picker__group').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          if (Number.isNaN(position)) return;

          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const optionEl = input.closest('.product-v2-variant-picker__option');
            const state = this.getOptionState(position, input.value, selectedByPosition);
            const shouldHide = state === 'unavailable' && hasSelectionInOtherGroup(position);

            if (optionEl) optionEl.hidden = shouldHide;
            input.classList.toggle('disabled', !shouldHide && state === 'sold-out');
          });
        });
      }

      buildSectionRequestUrl(productUrl) {
        return `${productUrl}${productUrl.includes('?') ? '&' : '?'}section_id=${this.sectionId}`;
      }

      cacheSectionResponse(requestUrl, responseText) {
        sectionResponseCache.set(requestUrl, responseText);

        if (sectionResponseCache.size > SECTION_CACHE_MAX) {
          const oldestKey = sectionResponseCache.keys().next().value;
          sectionResponseCache.delete(oldestKey);
        }
      }

      initColorPrefetch() {
        this.querySelectorAll('.product-v2-colors__swatch:not(.is-active), .product-v2-styles__option:not(.is-active)').forEach((link) => {
          link.addEventListener('mouseenter', this.onColorPrefetch, { once: true, passive: true });
          link.addEventListener('focus', this.onColorPrefetch, { once: true, passive: true });
        });
      }

      onColorPrefetch(event) {
        const swatch = event.currentTarget;
        const productUrl = swatch?.getAttribute('href');
        if (!productUrl) return;

        const requestUrl = this.buildSectionRequestUrl(productUrl);
        if (sectionResponseCache.has(requestUrl)) return;

        fetch(requestUrl, { priority: 'low' })
          .then((response) => {
            if (!response.ok) return null;
            return response.text();
          })
          .then((responseText) => {
            if (responseText) this.cacheSectionResponse(requestUrl, responseText);
          })
          .catch(() => {});
      }

      runInitialSync(savedOptions = null) {
        if (savedOptions) {
          this.restoreSelectedOptions(savedOptions);
        }

        let changed = true;
        let passes = 0;

        while (changed && passes < 4) {
          changed = false;
          passes += 1;
          this.updateOptionAvailability();

          if (this.clearInvalidSelections()) changed = true;
          if (this.autoSelectDefaultOptions()) changed = true;
        }

        const matchedVariant = this.findMatchingVariant();
        this.syncBuyRowState(matchedVariant);

        if (matchedVariant?.id) {
          this.updateURL(this.dataset.url, matchedVariant.id);
        }
      }

      onColorClick(event) {
        const productLink = event.target.closest('.product-v2-colors__swatch, .product-v2-styles__option');
        if (!productLink || !this.contains(productLink)) return;

        if (productLink.classList.contains('is-active')) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        const productUrl = productLink.getAttribute('href');
        if (!productUrl) return;

        window.scrollTo({ top: 0, behavior: 'instant' });

        const savedOptions = this.getSelectedOptionsByPosition();
        const requestUrl = this.buildSectionRequestUrl(productUrl);
        this.renderSection(requestUrl, {
          fullReplace: true,
          savedOptions: Object.keys(savedOptions).length ? savedOptions : null,
        });
      }

      fetchPrice(variantId) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const requestUrl = `${this.dataset.url}?section_id=${this.sectionId}&variant=${variantId}`;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Price render failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newSection = html.querySelector('product-v2');
            if (!newSection) return;
            this.updatePriceOnly(newSection);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      loadSectionHtml(requestUrl, { fullReplace = false } = {}) {
        const cachedResponse = fullReplace ? sectionResponseCache.get(requestUrl) : null;

        if (cachedResponse) {
          return Promise.resolve(cachedResponse);
        }

        return fetch(requestUrl, { signal: this.abortController.signal, priority: 'high' })
          .then((response) => {
            if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (fullReplace) {
              this.cacheSectionResponse(requestUrl, responseText);
            }

            return responseText;
          });
      }

      renderSection(requestUrl, { focusTargetId = null, fullReplace = false, savedOptions = null } = {}) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.classList.add('is-loading');

        this.loadSectionHtml(requestUrl, { fullReplace })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newSection = html.querySelector('product-v2');

            if (!newSection) {
              throw new Error('product-v2 not found in section response');
            }

            const productUrl = new URL(requestUrl, window.location.origin);
            productUrl.searchParams.delete('section_id');
            productUrl.searchParams.delete('option_values');
            productUrl.searchParams.delete('v2_selection_complete');
            productUrl.searchParams.delete('variant');
            const cleanUrl = productUrl.pathname + productUrl.search;

            if (fullReplace) {
              this.updateURL(cleanUrl, null);
              newSection.dataset.skipInitialSync = 'true';

              HTMLUpdateUtility.viewTransition(this, newSection, [], [
                (newNode) => {
                  newNode.productVariants = null;
                  window?.Shopify?.PaymentButton?.init();
                  newNode.classList.remove('is-loading');
                  newNode.runInitialSync(savedOptions);
                  newNode.initColorPrefetch();
                  newNode.scrollToTopAfterColorChange();
                },
              ]);
              return;
            }

            this.classList.remove('is-loading');

            if (focusTargetId) {
              this.querySelector(`#${focusTargetId}`)?.focus();
            }
          })
          .catch((error) => {
            this.classList.remove('is-loading');

            if (error.name === 'AbortError') return;

            console.error(error);
            const fallbackUrl = requestUrl.split('?')[0];
            window.location.assign(fallbackUrl);
          });
      }

      updatePriceOnly(newSection) {
        const sectionId = this.sectionId;
        const source = newSection.querySelector(`#price-${sectionId}`);
        const destination = this.querySelector(`#price-${sectionId}`);
        if (!source || !destination) return;

        destination.innerHTML = source.innerHTML;
      }

      syncStickyAtcState(state) {
        const stickyAtc = this.querySelector('product-v2-sticky-atc');
        if (typeof stickyAtc?.syncState !== 'function') return;
        stickyAtc.syncState(state);
      }

      syncBuyRowState(variant) {
        const sectionId = this.sectionId;
        const buyRow = this.querySelector('.product-v2-buy-row');
        const submitButton = this.querySelector(`#ProductSubmitButton-${sectionId}`);
        const submitButtonText = submitButton?.querySelector('span');
        const variantInput = this.querySelector(`#product-form-${sectionId} input[name="id"]`);
        const quantityInput = this.querySelector(`#Quantity-${sectionId}`);
        const quantityButtons = this.querySelectorAll(`#Quantity-Form-${sectionId} .quantity__button`);
        const selectSizeText = buyRow?.dataset.selectSizeText || 'SELECT A SIZE';
        const requiredCount = this.getRequiredOptionCount();
        const selectedCount = this.getSelectedOptionCount();
        const resolvedVariant = variant || this.findMatchingVariant();
        const hasAllOptionsSelected = requiredCount > 0 && selectedCount >= requiredCount;

        if (!submitButton || !submitButtonText) return;

        if (!hasAllOptionsSelected) {
          buyRow?.classList.remove('is-variant-ready');
          submitButton.disabled = true;
          submitButtonText.textContent = selectSizeText;

          if (variantInput) {
            variantInput.value = '';
            variantInput.disabled = true;
          }

          if (quantityInput) quantityInput.disabled = true;
          quantityButtons.forEach((button) => {
            button.disabled = true;
          });

          this.syncStickyAtcState({
            hasAllOptionsSelected,
            resolvedVariant,
            isAvailable: false,
            selectSizeText,
          });
          return;
        }

        if (!resolvedVariant?.id) {
          buyRow?.classList.remove('is-variant-ready');
          submitButton.disabled = true;
          submitButtonText.textContent = window.variantStrings?.unavailable || 'Unavailable';

          if (variantInput) {
            variantInput.value = '';
            variantInput.disabled = true;
          }

          if (quantityInput) quantityInput.disabled = true;
          quantityButtons.forEach((button) => {
            button.disabled = true;
          });

          this.syncStickyAtcState({
            hasAllOptionsSelected,
            resolvedVariant,
            isAvailable: false,
            selectSizeText,
          });
          return;
        }

        buyRow?.classList.add('is-variant-ready');

        if (quantityInput) quantityInput.disabled = false;
        quantityButtons.forEach((button) => {
          button.disabled = false;
        });

        if (variantInput) {
          variantInput.value = resolvedVariant.id;
          variantInput.disabled = !this.isVariantAvailable(resolvedVariant);
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.isVariantAvailable(resolvedVariant)) {
          submitButton.disabled = false;
          submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          window?.Shopify?.PaymentButton?.init();
        } else {
          submitButton.disabled = true;
          submitButtonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }

        this.syncStickyAtcState({
          hasAllOptionsSelected,
          resolvedVariant,
          isAvailable: this.isVariantAvailable(resolvedVariant),
          selectSizeText,
        });
      }

      scrollToTopAfterColorChange() {
        const root = document.documentElement;
        root.style.setProperty('overflow-anchor', 'none');

        const lockScrollTop = () => {
          window.scrollTo({ top: 0, behavior: 'instant' });
        };

        lockScrollTop();

        requestAnimationFrame(() => {
          lockScrollTop();
          root.style.removeProperty('overflow-anchor');
        });

        const galleryImages = this.querySelectorAll('.product-v2-gallery__image');
        const pendingImages = [...galleryImages].filter((img) => !img.complete);

        if (!pendingImages.length) return;

        pendingImages.forEach((img) => {
          img.addEventListener('load', lockScrollTop, { once: true });
          img.addEventListener('error', lockScrollTop, { once: true });
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const nextUrl = new URL(url, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        const managedParams = ['variant', 'section_id', 'option_values', 'v2_selection_complete'];

        for (const [key, value] of currentParams.entries()) {
          if (managedParams.includes(key)) continue;
          if (!nextUrl.searchParams.has(key)) {
            nextUrl.searchParams.set(key, value);
          }
        }

        managedParams.forEach((key) => nextUrl.searchParams.delete(key));

        if (variantId) {
          nextUrl.searchParams.set('variant', variantId);
        }

        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`);
      }

      get sectionId() {
        return this.dataset.section;
      }
    }
  );
}
